import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SeoHead from '../../../shared/seo/SeoHead';
import { FauxFurPomPomFaqSeo } from '../../pompom/seo/FauxFurPomPomFaqSeo';
import '../../pompom/styles/fauxFurPomPomStyle.css';

/* ── Category metadata ───────────────────────────────── */
interface CatMeta { color: string; bg: string; border: string; icon: React.ReactNode; }

const CAT_META: Record<string, CatMeta> = {
    "All": {
        color: "#6d28d9", bg: "rgba(109,40,217,0.08)", border: "rgba(109,40,217,0.22)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    },
    "About": {
        color: "#1d4ed8", bg: "rgba(29,78,216,0.07)", border: "rgba(29,78,216,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    },
    "Materials": {
        color: "#047857", bg: "rgba(4,120,87,0.07)", border: "rgba(4,120,87,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
    },
    "Sizes & Custom": {
        color: "#b45309", bg: "rgba(180,83,9,0.07)", border: "rgba(180,83,9,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 6H3"/><path d="M21 12H3"/><path d="M21 18H3"/></svg>,
    },
    "Wholesale": {
        color: "#be185d", bg: "rgba(190,24,93,0.07)", border: "rgba(190,24,93,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    },
    "Shipping": {
        color: "#0e7490", bg: "rgba(14,116,144,0.07)", border: "rgba(14,116,144,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    },
    "Care": {
        color: "#9333ea", bg: "rgba(147,51,234,0.07)", border: "rgba(147,51,234,0.2)",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    },
};

const CATEGORIES = ["All", "About", "Materials", "Sizes & Custom", "Wholesale", "Shipping", "Care"];

const HERO_STATS = [
    { value: "6",    label: "Standard Sizes" },
    { value: "200+", label: "Colors" },
    { value: "50+",  label: "Countries" },
    { value: "100%", label: "Cruelty-Free" },
    { value: "17",   label: "Questions Answered" },
];

interface FaqItem {
    id: number;
    category: string;
    question: string;
    answer: React.ReactNode;
    schemaText: string;
}

const FAQ_ITEMS: FaqItem[] = [
    {
        id: 1, category: "About",
        question: "What are faux fur pom poms?",
        schemaText: "Faux fur pom poms are decorative fluffy balls crafted from synthetic polyester faux fur fabric. Each pom pom is hand-cut and hand-sewn around a compressed inner core, resulting in a full, soft, shaggy ball. They are used to embellish hats, scarves, bags, keychains, and winter accessories. Unlike yarn pom poms, faux fur pom poms have a realistic fur-like texture with a premium appearance.",
        answer: (
            <>
                <p><strong>Faux fur pom poms</strong> are decorative fluffy balls crafted from synthetic polyester faux fur fabric. Each pom pom is hand-cut and hand-sewn around a compressed inner core, resulting in a full, soft, shaggy ball that replicates the luxurious appearance of real fur — without any animal materials.</p>
                <p>They are used to embellish <strong>hats, beanies, scarves, bags, keychains, and winter accessories</strong>. Unlike yarn pom poms, faux fur pom poms have a realistic fur-like texture with a premium, high-end appearance favoured by fashion brands and boutique retailers worldwide.</p>
                <div className="ffpf-highlight">A single faux fur pom pom can transform a plain beanie into a luxury accessory — that's why premium knitwear brands choose them.</div>
            </>
        ),
    },
    {
        id: 2, category: "About",
        question: "How are faux fur pom poms different from yarn pom poms?",
        schemaText: "Yarn pom poms are made by winding yarn around a template, then cutting and trimming — giving a light, textured ball. Faux fur pom poms are made from synthetic fur fabric cut into strips, then gathered and sewn around a stuffed core — producing a denser, fluffier ball with a plush fur-like appearance. Faux fur pom poms are considered more premium and are widely used in luxury hat and accessory design.",
        answer: (
            <>
                <p><strong>Yarn pom poms</strong> are made by winding yarn around a template, then cutting and trimming — producing a light, textured ball with a traditional craft look. <strong>Faux fur pom poms</strong> are made from synthetic fur fabric cut into strips, gathered and sewn around a stuffed core — producing a denser, fluffier ball with a plush, fur-like appearance.</p>
                <p>Faux fur pom poms are considered <strong>more premium</strong> and are widely used in luxury hat and accessory design. They hold their shape better, have a richer visual weight, and are highly favoured by fashion designers and premium knitwear brands.</p>
            </>
        ),
    },
    {
        id: 3, category: "About",
        question: "What products are faux fur pom poms used for?",
        schemaText: "Faux fur pom poms are used on winter hats and beanies as topper decorations; on scarves and shawls as end tassels; as bag charms and handbag decorations; as keychain accessories; as gift wrapping embellishments; and as home décor. They are also used by fashion designers as runway accessories.",
        answer: (
            <>
                <p>Faux fur pom poms are used across a wide range of fashion and accessory products:</p>
                <ul>
                    <li><strong>Winter hats and beanies</strong> — as a hat topper sewn or clipped to the crown</li>
                    <li><strong>Scarves and shawls</strong> — as end tassels or scattered accent decorations</li>
                    <li><strong>Handbag and purse charms</strong> — attached to bag handles or zippers</li>
                    <li><strong>Keychain accessories</strong> — as a standalone fluffy keyring decoration</li>
                    <li><strong>Gift wrapping</strong> — as a premium ribbon alternative on gift boxes</li>
                    <li><strong>Home décor</strong> — on cushions, wreaths, and seasonal display pieces</li>
                    <li><strong>Fashion runway accessories</strong> — used by designers in seasonal collections</li>
                </ul>
            </>
        ),
    },
    {
        id: 4, category: "Materials",
        question: "What materials are used to make your faux fur pom poms?",
        schemaText: "Our faux fur pom poms are made from high-quality synthetic polyester faux fur fabric. We stock two main pile types: short pile (5–10mm) which produces a dense, structured finish; and long pile (20–40mm) which creates a dramatic, fluffy appearance. The inner core is compressed polyester wadding. All materials are OEKO-TEX Standard 100 certified.",
        answer: (
            <>
                <p>Our faux fur pom poms use <strong>high-quality synthetic polyester faux fur fabric</strong>. We stock two main pile types:</p>
                <ul>
                    <li><strong>Short pile (5–10mm)</strong> — dense, structured, velvet-like finish; ideal for smaller pom poms and bag charms</li>
                    <li><strong>Long pile (20–40mm)</strong> — dramatic, fluffy, shaggy appearance; ideal for hat toppers and oversized statement pieces</li>
                </ul>
                <p>The inner core is filled with <strong>compressed polyester wadding</strong> for shape retention and consistent roundness. All materials are <strong>OEKO-TEX Standard 100 certified</strong> — free from harmful substances and safe for skin contact.</p>
            </>
        ),
    },
    {
        id: 5, category: "Materials",
        question: "Are your faux fur pom poms cruelty-free?",
        schemaText: "Yes — 100% cruelty-free. Our faux fur pom poms contain absolutely no animal-derived materials. They are made entirely from synthetic polyester fibers. All dyes are non-toxic, OEKO-TEX certified, and safe for skin contact.",
        answer: (
            <>
                <p>Yes — <strong>100% cruelty-free</strong>. Our faux fur pom poms contain absolutely no animal-derived materials. They are made entirely from <strong>synthetic polyester fibers</strong> that replicate the look and feel of real fur without harming any animals.</p>
                <p>All dyes are <strong>non-toxic, OEKO-TEX certified</strong>, and safe for skin contact. Our products are fully vegan and suitable for brands with cruelty-free certification requirements.</p>
                <div className="ffpf-highlight">We provide OEKO-TEX certificates and cruelty-free declarations upon request for wholesale buyers.</div>
            </>
        ),
    },
    {
        id: 6, category: "Materials",
        question: "How many colors are available?",
        schemaText: "We stock 200+ standard colors including solid shades, melange blends, ombre gradients, and metallic finishes. For wholesale orders of 1,000 pieces or more per color, we offer custom Pantone color matching.",
        answer: (
            <>
                <p>We stock <strong>200+ standard colors</strong> including:</p>
                <ul>
                    <li><strong>Solid shades</strong> — full spectrum from white, black, and neutrals to vivid brights</li>
                    <li><strong>Melange blends</strong> — two-tone mixed fibers for a heathered, natural look</li>
                    <li><strong>Ombre gradients</strong> — dual-tone graduated color on a single pom pom</li>
                    <li><strong>Metallic finishes</strong> — gold, silver, rose gold, and holographic fibers woven through</li>
                    <li><strong>Seasonal collections</strong> — updated each spring/summer and autumn/winter season</li>
                </ul>
                <p>For wholesale orders of <strong>1,000+ pieces per color</strong>, we offer custom <strong>Pantone color matching</strong>. A free color swatch approval is provided before any custom dyeing begins.</p>
            </>
        ),
    },
    {
        id: 7, category: "Sizes & Custom",
        question: "What standard sizes are available?",
        schemaText: "Standard sizes by diameter: 5cm (bag charms), 6cm (beanies), 8cm (most popular for hats), 10cm (bold statement), 12cm (oversized hat topper), 15cm (extra large, décor). Custom sizes from 3cm to 20cm are available for bulk orders of 500 pieces or more.",
        answer: (
            <>
                <p>Our standard faux fur pom pom sizes (measured by diameter):</p>
                <ul>
                    <li><strong>5 cm</strong> — ideal for bag charms, keychain decorations, and small accessories</li>
                    <li><strong>6 cm</strong> — popular for beanies, knitted hats, and children's accessories</li>
                    <li><strong>8 cm</strong> — the most popular size for adult winter hats and fashion beanies</li>
                    <li><strong>10 cm</strong> — bold statement pompom for oversized hats and scarves</li>
                    <li><strong>12 cm</strong> — oversized, premium hat topper for luxury knitwear brands</li>
                    <li><strong>15 cm</strong> — extra large, for display, home décor, and gift wrapping</li>
                </ul>
                <p>Custom sizes from <strong>3 cm to 20 cm</strong> are available for bulk orders of 500 pieces or more.</p>
            </>
        ),
    },
    {
        id: 8, category: "Sizes & Custom",
        question: "Can I order custom sizes and shapes?",
        schemaText: "Yes. Custom sizes from 3cm to 20cm in diameter are available from a minimum of 500 pieces per size. We also produce oval pompoms, teardrop shapes, and flat disc forms. A physical sample is provided at no charge before full production begins.",
        answer: (
            <>
                <p>Yes — custom sizes from <strong>3 cm to 20 cm</strong> in diameter are available from a minimum of 500 pieces per size. We also produce non-round variants:</p>
                <ul>
                    <li><strong>Oval pom poms</strong> — elongated shape for scarf ends and garment trims</li>
                    <li><strong>Teardrop pom poms</strong> — pointed base for cleaner attachment to accessories</li>
                    <li><strong>Flat disc pom poms</strong> — shorter pile for a dense, button-like decorative element</li>
                </ul>
                <p>Submit your size specifications and we provide a <strong>physical sample for approval at no charge</strong> before full production begins.</p>
            </>
        ),
    },
    {
        id: 9, category: "Sizes & Custom",
        question: "How does the custom order process work?",
        schemaText: "The custom order process: 1. Inquiry — share color, size, quantity. 2. Quotation — price quote within 24 hours. 3. Sample — free physical sample shipped for approval. 4. Approval & Payment — confirm and pay advance. 5. Production & Dispatch — completed within agreed timeline.",
        answer: (
            <>
                <p>Our custom order process is straightforward and transparent:</p>
                <ol>
                    <li><strong>Inquiry</strong> — share your required color, size, quantity, and reference images via our contact form or email</li>
                    <li><strong>Quotation</strong> — we send a detailed price quote within 24 hours</li>
                    <li><strong>Sample</strong> — a free physical sample is produced and shipped for your approval</li>
                    <li><strong>Approval &amp; Payment</strong> — confirm the sample and make the advance payment</li>
                    <li><strong>Production &amp; Dispatch</strong> — completed and dispatched with full tracking within the agreed timeline</li>
                </ol>
                <div className="ffpf-highlight">No production ever starts without your explicit sample approval — your satisfaction is guaranteed from the first piece.</div>
            </>
        ),
    },
    {
        id: 10, category: "Wholesale",
        question: "What is the minimum order quantity (MOQ)?",
        schemaText: "MOQ: Standard in-stock colors — 500 pieces per color per size; Custom Pantone color — 1,000 pieces per color; Custom size — 500 pieces; Private label packaging — 2,000 pieces minimum; Sample order — 10 to 20 pieces. Volume pricing breaks at 1,000, 5,000, and 10,000+ pieces.",
        answer: (
            <>
                <p>Minimum order quantities depend on the order type:</p>
                <ul>
                    <li><strong>Standard in-stock colors</strong> — 500 pieces per color, per size</li>
                    <li><strong>Custom Pantone color</strong> — 1,000 pieces per color</li>
                    <li><strong>Custom size (non-standard)</strong> — 500 pieces minimum</li>
                    <li><strong>Private label packaging</strong> — 2,000 pieces minimum</li>
                    <li><strong>Sample order</strong> — 10 to 20 pieces available before committing to bulk</li>
                </ul>
                <p>Volume pricing breaks apply at <strong>1,000 / 5,000 / 10,000+ pieces</strong> with increasing discounts at each tier.</p>
            </>
        ),
    },
    {
        id: 11, category: "Wholesale",
        question: "What are the wholesale prices?",
        schemaText: "Indicative rates for 8cm standard faux fur pom poms: 500–999 pieces — ₹18 to ₹28 per piece; 1,000–4,999 pieces — ₹14 to ₹22 per piece; 5,000–9,999 pieces — 10% discount; 10,000+ pieces — 20% discount or custom quote.",
        answer: (
            <>
                <p>Pricing depends on size, pile type, and order quantity. Indicative rates for <strong>8 cm standard faux fur pom poms</strong>:</p>
                <ul>
                    <li><strong>500–999 pieces</strong> — ₹18 to ₹28 per piece</li>
                    <li><strong>1,000–4,999 pieces</strong> — ₹14 to ₹22 per piece</li>
                    <li><strong>5,000–9,999 pieces</strong> — 10% discount automatically applied</li>
                    <li><strong>10,000+ pieces</strong> — 20% discount or custom quoted price</li>
                </ul>
                <p>Contact us with your exact color, size, and quantity for a <strong>formal proforma invoice within 24 hours</strong>. All prices are quoted ex-works India; shipping costs are additional.</p>
            </>
        ),
    },
    {
        id: 12, category: "Wholesale",
        question: "Do you offer private label packaging?",
        schemaText: "Yes. Private label packaging from 2,000 pieces: custom hang tags, individual poly bags with branding, master carton labelling, retail-ready packaging. Net 30 payment terms available for established wholesale accounts.",
        answer: (
            <>
                <p>Yes — <strong>private label packaging</strong> is available from a minimum of 2,000 pieces. Options include:</p>
                <ul>
                    <li>Custom <strong>hang tags</strong> with your brand name, logo, and care instructions</li>
                    <li>Individual <strong>poly bags</strong> with your branding and barcode</li>
                    <li>Master <strong>carton labelling</strong> with your company details and packing list</li>
                    <li><strong>Retail-ready packaging</strong> for boutique and department store buyers</li>
                </ul>
                <p>Dedicated account management and <strong>Net 30 payment terms</strong> are available for established wholesale accounts after three successful orders.</p>
            </>
        ),
    },
    {
        id: 13, category: "Shipping",
        question: "Which countries do you ship to?",
        schemaText: "We ship to 50+ countries. We regularly supply customers in the USA, UK, Canada, Australia, Germany, France, UAE, Saudi Arabia, Japan, South Korea, and across Europe and Southeast Asia. DDP shipping available for USA, UK, and EU.",
        answer: (
            <>
                <p>We ship to <strong>50+ countries worldwide</strong>. We regularly supply customers in the <strong>USA, UK, Canada, Australia, Germany, France, UAE, Saudi Arabia, Japan, South Korea</strong>, and across Europe and Southeast Asia.</p>
                <p>All orders include a <strong>shipment tracking number</strong> sent by email upon dispatch. <strong>DDP (Delivered Duty Paid)</strong> shipping is available for the USA, UK, and EU — no hidden import fees for buyers in these regions.</p>
            </>
        ),
    },
    {
        id: 14, category: "Shipping",
        question: "What are the typical delivery timelines?",
        schemaText: "Production time is 10 to 21 business days. Delivery after dispatch: India — 3 to 5 days; USA/UK/Canada/Australia — 7 to 14 days; UAE — 5 to 10 days; EU — 8 to 14 days; Large bulk orders — sea freight 25 to 35 days.",
        answer: (
            <>
                <p>Production time is <strong>10 to 21 business days</strong> depending on order size. Delivery after dispatch:</p>
                <ul>
                    <li><strong>India (domestic)</strong> — 3 to 5 business days</li>
                    <li><strong>USA, UK, Canada, Australia</strong> — 7 to 14 business days via air freight</li>
                    <li><strong>UAE and Gulf countries</strong> — 5 to 10 business days</li>
                    <li><strong>Europe (EU)</strong> — 8 to 14 business days</li>
                    <li><strong>Large bulk orders (10,000+ pieces)</strong> — sea freight in 25 to 35 days at lower cost</li>
                </ul>
                <p>Express shipping (3–5 days worldwide) via DHL is available at additional cost for urgent orders.</p>
            </>
        ),
    },
    {
        id: 15, category: "Shipping",
        question: "What shipping carriers do you use?",
        schemaText: "We use DHL Express (3–5 days), FedEx International (4–7 days), Registered Airmail (10–21 days), and sea freight for bulk orders. All shipments include full tracking details sent by email.",
        answer: (
            <>
                <p>We use established, reliable international carriers:</p>
                <ul>
                    <li><strong>DHL Express</strong> — fastest option, 3–5 days internationally</li>
                    <li><strong>FedEx International</strong> — reliable mid-speed delivery, 4–7 business days</li>
                    <li><strong>Registered Airmail</strong> — economy option, 10–21 business days</li>
                    <li><strong>Sea freight</strong> — for bulk orders of 10,000+ pieces where cost matters more than speed</li>
                </ul>
                <p>All shipments include <strong>full tracking details</strong> sent by email upon dispatch. Import duties and customs fees are the buyer's responsibility unless DDP shipping is selected.</p>
            </>
        ),
    },
    {
        id: 16, category: "Care",
        question: "How do I care for faux fur pom poms?",
        schemaText: "Spot clean with a damp cloth and mild soap. Hand wash in cold water (max 30°C) if needed. Never tumble dry or apply direct heat — high heat melts synthetic fibers. No bleach. Store loosely in a cool, dry place.",
        answer: (
            <>
                <p>Faux fur pom poms are low maintenance with these simple care steps:</p>
                <ol>
                    <li><strong>Spot clean</strong> — dab gently with a damp cloth and mild soap for surface marks. Do not soak.</li>
                    <li><strong>Hand wash if needed</strong> — cold water (max 30°C) on a gentle cycle. Lay flat to air dry.</li>
                    <li><strong>Never tumble dry</strong> — high heat melts synthetic fibers and ruins the fur texture.</li>
                    <li><strong>No bleach</strong> — degrades polyester fibers and causes color loss.</li>
                    <li><strong>No direct iron</strong> — never apply a hot iron directly to faux fur.</li>
                    <li><strong>Store loosely</strong> — keep in a cool, dry place away from direct sunlight. Do not compress under heavy items.</li>
                </ol>
            </>
        ),
    },
    {
        id: 17, category: "Care",
        question: "How do I restore the fluffiness of a faux fur pom pom?",
        schemaText: "Shake vigorously to loosen fibers. Use a soft-bristle pet brush or wide-tooth comb to brush through in all directions. Mist lightly with water spray, then shake and brush while damp. Air dry fully. Avoid steam or direct heat.",
        answer: (
            <>
                <p>To restore a flattened faux fur pom pom:</p>
                <ol>
                    <li><strong>Shake vigorously</strong> — hold by the base and shake firmly to loosen compressed fibers</li>
                    <li><strong>Brush gently</strong> — use a soft-bristle pet brush or wide-tooth comb, brushing in all directions from base outward</li>
                    <li><strong>Mist and reshape</strong> — lightly mist with water spray, then shake and brush while damp; fibers reset to their original shape as they dry</li>
                    <li><strong>Air dry fully</strong> — allow to air dry completely before use or storage</li>
                </ol>
                <div className="ffpf-highlight">Avoid steam or direct heat — synthetic fibers can melt or permanently kink at high temperatures.</div>
            </>
        ),
    },
];

const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.schemaText },
    })),
};

const getCatCount = (cat: string) =>
    cat === "All" ? FAQ_ITEMS.length : FAQ_ITEMS.filter((f) => f.category === cat).length;

/* ── Component ───────────────────────────────────────── */
const FauxFurPomPomsFaqPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState("All");
    const [openId, setOpenId] = useState<number | null>(null);

    const filtered = activeCategory === "All"
        ? FAQ_ITEMS
        : FAQ_ITEMS.filter((f) => f.category === activeCategory);

    const toggle = (id: number) => setOpenId((prev) => (prev === id ? null : id));
    const selectCat = (cat: string) => { setActiveCategory(cat); setOpenId(null); };

    return (
        <div className="ffpf-page">
            <SeoHead {...FauxFurPomPomFaqSeo} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }} />

            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="ffpf-breadcrumb-nav">
                <ol className="ffpf-breadcrumb" itemScope itemType="https://schema.org/BreadcrumbList">
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" itemProp="item"><span itemProp="name">Home</span></a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li><span className="ffpf-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/faux-fur-pom-poms" itemProp="item"><span itemProp="name">Faux Fur Pom Poms</span></a>
                        <meta itemProp="position" content="2" />
                    </li>
                    <li><span className="ffpf-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span itemProp="name" aria-current="page">FAQ</span>
                        <meta itemProp="position" content="3" />
                    </li>
                </ol>
            </nav>

            {/* Hero */}
            <section className="ffpf-hero">
                <div className="ffpf-hero-bg" aria-hidden="true" />
                <div className="ffpf-blob ffpf-blob--1" aria-hidden="true" />
                <div className="ffpf-blob ffpf-blob--2" aria-hidden="true" />
                <div className="ffpf-blob ffpf-blob--3" aria-hidden="true" />
                <div className="ffpf-hero-dots" aria-hidden="true">
                    {Array.from({ length: 30 }).map((_, i) => <span key={i} />)}
                </div>

                <div className="ffpf-hero-inner">
                    <div className="ffpf-hero-tag">
                        <span className="ffpf-hero-tag-dot" aria-hidden="true" />
                        Faux Fur Pom Poms · FAQ
                    </div>
                    <h1>
                        Everything You Need<br />
                        to Know About{" "}
                        <span className="ffpf-hero-accent">Faux Fur Pom Poms</span>
                    </h1>
                    <p className="ffpf-hero-sub">
                        Materials, sizes, wholesale pricing, and custom orders —<br className="ffpf-hero-br" />
                        answered by Arte Craft India's manufacturing team.
                    </p>
                    <div className="ffpf-hero-stats">
                        {HERO_STATS.map((s) => (
                            <div key={s.label} className="ffpf-hero-stat">
                                <div className="ffpf-hero-stat-val">{s.value}</div>
                                <div className="ffpf-hero-stat-lbl">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ffpf-hero-wave" aria-hidden="true">
                    <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0,32 C240,64 480,0 720,32 C960,64 1200,0 1440,32 L1440,64 L0,64 Z" fill="#f8f7fc" />
                    </svg>
                </div>
            </section>

            {/* Mobile filter tabs */}
            <div className="ffpf-mobile-filters" role="navigation" aria-label="Filter by category">
                <div className="ffpf-mobile-filters-inner">
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat}
                            className={`ffpf-mobile-tab${activeCategory === cat ? " active" : ""}`}
                            style={activeCategory === cat ? {
                                background: CAT_META[cat].color,
                                borderColor: "transparent",
                            } : {}}
                            aria-pressed={activeCategory === cat}
                            onClick={() => selectCat(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Body: sidebar + content */}
            <div className="ffpf-body">
                <div className="ffpf-layout">

                    {/* Sidebar */}
                    <aside className="ffpf-sidebar" aria-label="FAQ categories">
                        <p className="ffpf-sidebar-label">Browse by Topic</p>
                        <nav className="ffpf-sidebar-nav">
                            {CATEGORIES.map((cat) => {
                                const meta = CAT_META[cat];
                                const count = getCatCount(cat);
                                const isActive = activeCategory === cat;
                                return (
                                    <button
                                        key={cat}
                                        className={`ffpf-sidebar-btn${isActive ? " active" : ""}`}
                                        style={isActive ? {
                                            background: meta.bg,
                                            borderColor: meta.border,
                                            color: meta.color,
                                        } : {}}
                                        onClick={() => selectCat(cat)}
                                        aria-pressed={isActive}
                                    >
                                        <span
                                            className="ffpf-sidebar-icon"
                                            style={isActive ? { color: meta.color } : {}}
                                        >
                                            {meta.icon}
                                        </span>
                                        <span className="ffpf-sidebar-cat">{cat}</span>
                                        <span
                                            className="ffpf-sidebar-count"
                                            style={isActive ? { background: meta.color, color: "#fff" } : {}}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>

                        <hr className="ffpf-sidebar-divider" aria-hidden="true" />
                        <div className="ffpf-sidebar-card">
                            <div className="ffpf-sidebar-card-icon" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                            </div>
                            <p className="ffpf-sidebar-card-text">Can't find your answer?</p>
                            <button className="ffpf-sidebar-card-btn" onClick={() => navigate("/contact")}>
                                Ask Us Directly
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                    </aside>

                    {/* FAQ content */}
                    <div className="ffpf-content">
                        <div className="ffpf-content-head">
                            <p className="ffpf-count">
                                <span>{filtered.length}</span>{" "}
                                question{filtered.length !== 1 ? "s" : ""}
                                {activeCategory !== "All" && (
                                    <span className="ffpf-count-cat">in {activeCategory}</span>
                                )}
                            </p>
                        </div>

                        {filtered.length === 0 ? (
                            <p className="ffpf-empty">No questions in this category yet.</p>
                        ) : (
                            <div className="ffpf-list">
                                {filtered.map((item, idx) => {
                                    const isOpen = openId === item.id;
                                    const meta = CAT_META[item.category];
                                    const displayNum = String(idx + 1).padStart(2, "0");
                                    return (
                                        <div
                                            key={item.id}
                                            className={`ffpf-item${isOpen ? " open" : ""}`}
                                            style={{
                                                "--cat-color":  meta.color,
                                                "--cat-bg":     meta.bg,
                                                "--cat-border": meta.border,
                                            } as React.CSSProperties}
                                        >
                                            <span className="ffpf-ghost-num" aria-hidden="true">{displayNum}</span>

                                            <button
                                                className="ffpf-question"
                                                onClick={() => toggle(item.id)}
                                                aria-expanded={isOpen}
                                            >
                                                <span className="ffpf-question-left">
                                                    <span className="ffpf-question-num">{displayNum}</span>
                                                    <span className="ffpf-question-text">{item.question}</span>
                                                </span>
                                                <span className="ffpf-chevron" aria-hidden="true">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                </span>
                                            </button>

                                            <div className={`ffpf-answer-wrap${isOpen ? " open" : ""}`}>
                                                <div className="ffpf-answer">
                                                    <div className="ffpf-answer-inner">
                                                        <span
                                                            className="ffpf-cat-pill"
                                                            style={{
                                                                color: meta.color,
                                                                background: meta.bg,
                                                                borderColor: meta.border,
                                                            }}
                                                        >
                                                            <span className="ffpf-cat-pill-icon">{meta.icon}</span>
                                                            {item.category}
                                                        </span>
                                                        <div className="ffpf-answer-body">{item.answer}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* CTA */}
            <section className="ffpf-cta">
                <div className="ffpf-cta-bg" aria-hidden="true" />
                <div className="ffpf-cta-blob ffpf-cta-blob--1" aria-hidden="true" />
                <div className="ffpf-cta-blob ffpf-cta-blob--2" aria-hidden="true" />
                <div className="ffpf-cta-inner">
                    <span className="ffpf-cta-tag">Get in Touch</span>
                    <h2>Ready to Order Faux Fur Pom Poms?</h2>
                    <p className="ffpf-cta-sub">
                        Share your color, size, and quantity — we'll send a custom quote and free sample within 24 hours.
                    </p>
                    <div className="ffpf-cta-btns">
                        <button className="ffpf-btn-primary" onClick={() => navigate("/contact")}>
                            Request a Free Quote
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                        <a href="https://wa.me/919818548369" target="_blank" rel="noreferrer" className="ffpf-btn-wa">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                <path d="M11.999 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.948-1.42A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                            </svg>
                            WhatsApp Us
                        </a>
                    </div>
                </div>
                <div className="ffpf-cta-deco" aria-hidden="true">
                    <div className="ffpf-cta-deco-ring ffpf-cta-deco-ring--1" />
                    <div className="ffpf-cta-deco-ring ffpf-cta-deco-ring--2" />
                    <div className="ffpf-cta-deco-ring ffpf-cta-deco-ring--3" />
                    <div className="ffpf-cta-deco-dot" />
                </div>
            </section>
        </div>
    );
};

export default FauxFurPomPomsFaqPage;
